/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, {useCallback} from 'react'
import {useTranslation} from 'react-i18next'
import {closeSecondaryModal, openSecondaryModal} from 'helpers/actions'
import {useAppDispatch} from 'helpers/store'
import UiAlertConfirmationModal from 'components/Ui/UiAlertConfirmationModal'
import LeadNoteForm from 'components/leads/forms/LeadNoteForm'
import LeadTaskForm from 'components/leads/forms/LeadTaskForm'
import {ModalSize} from 'types/enums/ModalSize'
import {LeadTaskStatus} from 'types/enums/LeadTaskStatus'
import type {Lead} from 'types/entities/Lead'
import type {LeadNote} from 'types/entities/LeadNote'
import type {LeadTask} from 'types/entities/LeadTask'
import Dates from 'utils/Dates'
import LeadsAction from 'store/leads/LeadsAction'
import OpportunitiesAction from 'store/opportunities/OpportunitiesAction'

type LeadAction = typeof LeadsAction | typeof OpportunitiesAction

interface UseEditLeadTaskNoteHandlersParams {
    selectedLead: Lead | null | undefined
    action: LeadAction
    onReload: () => void
}

export const useEditLeadTaskNoteHandlers = ({
    selectedLead,
    action,
    onReload,
}: UseEditLeadTaskNoteHandlersParams) => {
    const {t} = useTranslation()
    const dispatch = useAppDispatch()

    const handleAddTask = useCallback(() => {
        if (!selectedLead) return
        dispatch(
            openSecondaryModal(
                <LeadTaskForm lead={selectedLead} onChange={onReload} />,
                {modalSize: ModalSize.Small},
                t('New Task')
            )
        )
    }, [dispatch, selectedLead, onReload, t])

    const handleEditTask = useCallback((task: LeadTask) => {
        if (!selectedLead) return
        dispatch(
            openSecondaryModal(
                <LeadTaskForm lead={selectedLead} task={task} onChange={onReload} />,
                {modalSize: ModalSize.Small},
                t('Edit Task')
            )
        )
    }, [dispatch, selectedLead, onReload, t])

    const handleCompleteTask = useCallback((task: LeadTask) => {
        if (!selectedLead) return
        dispatch(
            openSecondaryModal(
                <UiAlertConfirmationModal
                    title={t('Complete Task')}
                    message={t('Are you sure you want to complete this task?')}
                    onConfirm={async () => {
                        const payload: Partial<LeadTask> = {
                            id: task.id,
                            lead_id: selectedLead.id,
                            seller_id: task.seller_id ?? selectedLead.seller_id,
                            title: task.title,
                            description: task.description ?? '',
                            type: task.type,
                            status: LeadTaskStatus.Done,
                            due_at: Dates.dayjs(task.due_at).format('YYYY-MM-DD HH:mm:ss') as unknown as LeadTask['due_at'],
                        }
                        await dispatch(action.fetchCreateLeadTask(payload))
                        dispatch(closeSecondaryModal())
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                {modalSize: ModalSize.Small},
                t('Confirm')
            )
        )
    }, [dispatch, selectedLead, action, t])

    const handleDeleteTask = useCallback((task: LeadTask) => {
        dispatch(
            openSecondaryModal(
                <UiAlertConfirmationModal
                    title={t('Delete Task')}
                    message={t('Are you sure you want to delete this task?')}
                    onConfirm={async () => {
                        await dispatch(action.fetchDeleteLeadTask(task))
                        dispatch(closeSecondaryModal())
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                {modalSize: ModalSize.Small},
                t('Confirm')
            )
        )
    }, [dispatch, action, t])

    const handleAddNote = useCallback(() => {
        if (!selectedLead) return
        dispatch(
            openSecondaryModal(
                <LeadNoteForm lead={selectedLead} onChange={onReload} />,
                {modalSize: ModalSize.Small},
                t('New Note')
            )
        )
    }, [dispatch, selectedLead, onReload, t])

    const handleEditNote = useCallback((note: LeadNote) => {
        if (!selectedLead) return
        dispatch(
            openSecondaryModal(
                <LeadNoteForm lead={selectedLead} note={note} onChange={onReload} />,
                {modalSize: ModalSize.Small},
                t('Edit Note')
            )
        )
    }, [dispatch, selectedLead, onReload, t])

    const handleDeleteNote = useCallback((note: LeadNote) => {
        dispatch(
            openSecondaryModal(
                <UiAlertConfirmationModal
                    title={t('Delete Note')}
                    message={t('Are you sure you want to delete this note?')}
                    onConfirm={async () => {
                        await dispatch(action.fetchDeleteLeadNote(note))
                        dispatch(closeSecondaryModal())
                    }}
                    onCancel={() => dispatch(closeSecondaryModal())}
                />,
                {modalSize: ModalSize.Small},
                t('Confirm')
            )
        )
    }, [dispatch, action, t])

    return {
        handleAddTask,
        handleEditTask,
        handleCompleteTask,
        handleDeleteTask,
        handleAddNote,
        handleEditNote,
        handleDeleteNote,
    }
}
